import { isDefined } from '@sgme/fp';
import Toast from 'react-bootstrap/Toast';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { selectCurrencyPairPrecision } from '@/api/config/selectors.ts';
import { useCurrentUserQuery } from '@/api/user/index.ts';
import { GrowlError } from '@/App/Growl/shared/GrowlError.tsx';
import { GrowlIntegrationDelay } from '@/App/Growl/shared/GrowlIntegrationDelay.tsx';
import { GrowlItem } from '@/App/Growl/shared/GrowlItem.tsx';
import { getExecutionColorAndIcon } from '@/App/Growl/utilities.ts';
import { FormattedFXDate } from '@/components/ui/FormattedFXDate.tsx';
import { Panel } from '@/components/ui/Panel/index.tsx';
import { blotterSlice } from '@/store/blotter/slice.ts';
import type { CashForwardDealExecution, CashNdfDealExecution } from '@/store/growl/state/cash.ts';
import { useAppSelector } from '@/store/hooks.ts';

export type GrowlCashForwardProps = {
  growlItem: CashForwardDealExecution | CashNdfDealExecution;
};

export const GrowlCashForward = (props: GrowlCashForwardProps) => {
  const { growlItem } = props;

  const { data: currentUser } = useCurrentUserQuery();

  const currencyPairPrecision = useAppSelector((state) =>
    selectCurrencyPairPrecision(state, `${growlItem.data.currency1}/${growlItem.data.currency2}`),
  );

  const accountName = isDefined(currentUser)
    ? currentUser.clients.find((client) => client.companyId === growlItem.data.accountId)?.companyName
    : undefined;

  const isExternalUser = isDefined(currentUser) && currentUser.userType === 'Trader';

  const [color, icon] = getExecutionColorAndIcon(growlItem.status);

  const tradeId = growlItem.status === 'confirmed' ? growlItem.tradeIds?.[0] : undefined;

  const blotterEntry = useAppSelector((state) =>
    isDefined(tradeId) ? blotterSlice.selectors.getCashEntryById(state, tradeId) : undefined,
  );

  const isSelling =
    (growlItem.data.notionalCurrencyIndex === 1 && growlItem.data.way === 'Bid') ||
    (growlItem.data.notionalCurrencyIndex === 2 && growlItem.data.way === 'Ask');

  const notionalCurrency =
    growlItem.data.notionalCurrencyIndex === 1 ? growlItem.data.currency1 : growlItem.data.currency2;

  const contraCurrency =
    growlItem.data.notionalCurrencyIndex === 1 ? growlItem.data.currency2 : growlItem.data.currency1;

  return (
    <>
      <Toast.Header className={`bg-${color} text-${color} bg-opacity-10`} data-e2e="growl-header">
        <i className="icon icon-sm me-2">{icon}</i>
        <strong className="me-auto">
          <FormattedMessage id={`growl.status.${growlItem.status}`} defaultMessage={growlItem.status ?? 'Unknown'} />
        </strong>
      </Toast.Header>

      <Toast.Body>
        <div className="text-start" data-e2e="growl-message">
          <FormattedMessage
            id={`growl.${growlItem.productName}.${growlItem.data.way}.${isExternalUser ? 'externalUser' : 'internalUser'}.message`}
            values={{
              currencyPair: `${growlItem.data.currency1}/${growlItem.data.currency2}`,
              notional: growlItem.data.notional,
              notionalCurrency,
              rate: growlItem.data.dealtRate,
            }}
          />
        </div>

        {growlItem.status === 'not-integrated' && <GrowlIntegrationDelay />}

        {growlItem.status === 'rejected' && (
          <GrowlError
            errorCode={growlItem.errorCode}
            multipassErrorCode={growlItem.multipassErrorCode}
            reason={growlItem.reason}
            sessionId={growlItem.sessionId}
          />
        )}

        <Panel labelId={'growl.Cash.dealDetails'} data-e2e="growl-details-toggle-button">
          <GrowlItem labelId="growl.Cash.item.notional" data-e2e="notional">
            <span data-e2e="notional-in-currency">
              <FormattedMessage
                id={isSelling ? 'growl.notional.Sell' : 'growl.notional.Buy'}
                values={{ notional: growlItem.data.notional, currency: notionalCurrency }}
              />
            </span>

            {growlItem.status === 'confirmed' && (
              <>
                <br />
                {isDefined(blotterEntry?.contraAmount) ? (
                  <span data-e2e="notional-in-contra-currency">
                    <FormattedMessage
                      id={isSelling ? 'growl.notional.Buy' : 'growl.notional.Sell'}
                      values={{ notional: blotterEntry.contraAmount, currency: contraCurrency }}
                    />
                  </span>
                ) : (
                  <span data-e2e="notional-in-inverted-currency">
                    <span className="blinker blinker-sm" />
                  </span>
                )}
              </>
            )}
          </GrowlItem>

          <GrowlItem labelId="growl.Cash.dealtRate" data-e2e="dealtRate">
            <FormattedNumber value={growlItem.data.dealtRate} minimumFractionDigits={currencyPairPrecision + 1} />
          </GrowlItem>

          <GrowlItem labelId="growl.Cash.forwardDate" data-e2e="forwardDate">
            <FormattedFXDate value={growlItem.data.forwardDate} dateStyle="medium" />
          </GrowlItem>

          {growlItem.productName === 'FxFwd' && (
            <>
              <GrowlItem labelId="growl.Cash.spotRate" data-e2e="spotRate">
                {growlItem.data.spotRate}
              </GrowlItem>
              <GrowlItem labelId="growl.Cash.swapPoints" data-e2e="swapPoints">
                {growlItem.data.swapPoints}
              </GrowlItem>
            </>
          )}

          {growlItem.productName === 'FxNdf' && (
            <>
              <GrowlItem labelId="growl.Cash.reference" data-e2e="reference">
                {growlItem.data.reference}
              </GrowlItem>

              <GrowlItem labelId="growl.Cash.fixingDate" data-e2e="fixingDate">
                <FormattedFXDate value={growlItem.data.fixingDate} dateStyle="medium" />
              </GrowlItem>
            </>
          )}

          <GrowlItem labelId="growl.Cash.account" data-e2e="account">
            {accountName}
          </GrowlItem>

          <GrowlItem labelId="growl.Cash.tradeDate" data-e2e="tradeDate">
            <FormattedFXDate value={growlItem.data.tradeDate} dateStyle="medium" withHours />
          </GrowlItem>

          <GrowlItem labelId="growl.Cash.tradeId" data-e2e="tradeId">
            {growlItem.status === 'confirmed' ? (
              growlItem.tradeIds.join(', ')
            ) : growlItem.status === 'not-integrated' ? (
              <FormattedMessage id="growl.tradeId.notYetIntegrated" />
            ) : (
              <FormattedMessage id="growl.Cash.tradeId.awaitingConfirmation" />
            )}
          </GrowlItem>
        </Panel>
      </Toast.Body>
    </>
  );
};
